import { action, autorun, computed, observable } from 'mobx';
import { directoryStore } from './DirectoryStore';
import { ZipApi } from '../lib/ZipApi';
import { getApiErrorMessage } from '../lib/ZipApiError';
import { showToasts } from '../lib/toaster';
import { DataSyncExecution } from '../types/DataSyncExecution';
import { DataTablePagingProps } from '@zaiusinc/hera';
import { DataSyncItem } from '../types/DataSyncItem';

export default class DataSyncDetailsStore {
  @observable public loading = false;
  @observable public executions: DataSyncExecution[] = [];
  @observable public dataSync?: DataSyncItem;

  private readonly maxTotal = 10000;
  @observable private _executionsPaging: DataTablePagingProps = {rowStart: 1, rowCount: 10, total: this.maxTotal};

  constructor(public dataSyncId: string) {
    autorun(() => {
      if (directoryStore.isReady) {
        this.fetch();
      }
    });
  }

  public async fetch() {
    try {
      this.loading = true;
      this.dataSync = undefined;
      const {dataSync} = (await ZipApi.getDataSync(this.dataSyncId));
      this.dataSync = dataSync;
    } catch (error: any) {
      const message = await getApiErrorMessage(error, 'Unable to load data sync');
      showToasts([{intent: 'danger', message}]);
    } finally {
      this.loading = false;
    }
  }

  @action
  public async getExecutions() {
    try {
      this.loading = true;
      this.executions = [];

      const offset = Math.floor(this._executionsPaging.rowStart / this._executionsPaging.rowCount);
      const response = await ZipApi.listDataSyncExecutions(
        this.dataSyncId,
        offset,
        this._executionsPaging.rowCount
      );

      this.executions = response.executions || [];

      if (this.executions.length < this._executionsPaging.rowCount) {
        this._executionsPaging.total = (this._executionsPaging.rowStart - 1) + this.executions.length;
      } else {
        this._executionsPaging.total = this.maxTotal;
      }
    } catch (error: any) {
      const message = await getApiErrorMessage(error, 'Unable to load executions');
      showToasts([{intent: 'danger', message}]);
    } finally {
      this.loading = false;
    }
  }

  @computed
  public get executionsPaging() {
    return this._executionsPaging;
  }

  @action
  public async setExecutionsPaging(paging: DataTablePagingProps) {
    if (paging.rowStart <= 0) {
      paging.rowStart = 1;
    }
    this._executionsPaging = paging;
    await this.getExecutions();
  }
}
